import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import dayjs, { type Dayjs } from 'dayjs';
import { r, s } from '@/theme/tokens';
import { Stack, ToggleGroup, ToggleItem } from '@/ui';
import { DatePicker, Popover, Text } from '@/ui/primitives';
import {
  cloneDatePickerOpenButtonSx,
  cloneDatePickerTextFieldSx,
  cloneDateQuickRangeTextSx,
  resolveCloneDatePreview,
} from './cloneTableToolbar.helpers';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';

type CloneTableDatePopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  dateQuickFilter: CloneDateQuickFilter;
  onDateQuickFilterChange: (next: CloneDateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
};

export const CloneTableDatePopover = ({
  open,
  anchorEl,
  onClose,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}: CloneTableDatePopoverProps): JSX.Element => {
  const quickFilterDays = dateQuickFilter === '14d' ? 14 : dateQuickFilter === '30d' ? 30 : 7;
  const { displayedFromDate, displayedToDate, isQuickRangePreview } = resolveCloneDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
  );
  const hasCustomDateRange = fromDate !== null || toDate !== null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      paperSx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${r('md')} !important`,
        overflow: 'hidden !important',
      }}
    >
      <Stack gap="none">
        <Stack
          gap="none"
          sx={{ px: s('lg'), py: s('xs'), borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap="sm"
            sx={{ '& .MuiSvgIcon-root': { color: 'text.secondary' } }}
          >
            <EventOutlinedIcon sx={{ width: '15px !important', height: '15px !important' }} />
            <Text role="label" color="textSecondary">
              Date Filters
            </Text>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap="md"
          sx={{ px: s('lg'), pt: s('sm'), pb: s('lg') }}
        >
          <Stack gap="none" align="flex-start">
            <Text role="label" color="textSecondary" sx={{ mb: `-${s('xs')}` }}>
              Quick Filters
            </Text>
            <ToggleGroup
              size="small"
              value={hasCustomDateRange ? null : dateQuickFilter}
              onChange={(_event, nextValue: CloneDateQuickFilter | null) => {
                if (!nextValue) return;
                onDateQuickFilterChange(nextValue);
              }}
              sx={{
                mt: `-${s('xs')}`,
              }}
            >
              <ToggleItem value="7d" sx={{ py: s('none'), height: 'auto' }}>
                <Text role="body" component="span">
                  7d
                </Text>
              </ToggleItem>
              <ToggleItem value="14d" sx={{ py: s('none'), height: 'auto' }}>
                <Text role="body" component="span">
                  14d
                </Text>
              </ToggleItem>
              <ToggleItem value="30d" sx={{ py: s('none'), height: 'auto' }}>
                <Text role="body" component="span">
                  30d
                </Text>
              </ToggleItem>
            </ToggleGroup>
          </Stack>
          <Stack gap="none" align="flex-start">
            <Text role="label" color="textSecondary" sx={{ mb: `-${s('xs')}` }}>
              From
            </Text>
            <DatePicker
              value={displayedFromDate}
              onChange={(nextValue) => {
                onFromDateChange(nextValue);
                if (nextValue !== null && toDate === null) {
                  onToDateChange(dayjs().startOf('day'));
                }
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              maxDate={toDate ?? dayjs()}
              openPickerButtonSx={cloneDatePickerOpenButtonSx(fromDate !== null)}
              textFieldSx={[
                cloneDatePickerTextFieldSx,
                ...(isQuickRangePreview ? [cloneDateQuickRangeTextSx] : []),
              ]}
            />
          </Stack>
          <Stack gap="none" align="flex-start">
            <Text role="label" color="textSecondary" sx={{ mb: `-${s('xs')}` }}>
              To
            </Text>
            <DatePicker
              value={displayedToDate}
              onChange={(nextValue) => {
                if (!nextValue) {
                  onToDateChange(nextValue);
                  return;
                }
                if (isQuickRangePreview) {
                  const nextEnd = nextValue.startOf('day');
                  const nextStart = nextEnd.subtract(quickFilterDays - 1, 'day');
                  onFromDateChange(nextStart);
                  onToDateChange(nextEnd);
                  return;
                }
                onToDateChange(nextValue);
              }}
              placeholder="dd/mm/yyyy"
              lowercasePlaceholder
              minDate={fromDate ?? undefined}
              maxDate={dayjs()}
              openPickerButtonSx={cloneDatePickerOpenButtonSx(toDate !== null)}
              textFieldSx={[
                cloneDatePickerTextFieldSx,
                ...(isQuickRangePreview ? [cloneDateQuickRangeTextSx] : []),
              ]}
            />
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
};
